import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { getCreatedToastMessage, getErrorToastMessage } from '@/libs/api'
import { QUERY_KEYS, STALE_TIME } from '@/libs/constants/hooks'
import type { Customer, CustomerListParams, HookParams, VehicleListParams } from '@/libs/interfaces'
import { customersApi } from '@/services/api/customers'
export function useCreateCustomer() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: customersApi.create,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.customers.all,
      })
      toast.success(getCreatedToastMessage(result, t('customers.customerAdded')))
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useCustomers({ params, enabled }: HookParams<CustomerListParams>) {
  return useQuery<Customer[]>({
    queryKey: QUERY_KEYS.customers.list(params),
    enabled: enabled ?? true,
    queryFn: async () => {
      return await customersApi.list(params)
    },
    staleTime: STALE_TIME['24Hours'], // 24 hour
  })
}

export function useCustomersByID({ params, enabled }: HookParams<Partial<Customer>>) {
  return useQuery<Customer>({
    queryKey: QUERY_KEYS.customers.id(params.driverID ?? ''),
    enabled: enabled ?? true,
    queryFn: async () => {
      return await customersApi.getByID(params)
    },
    staleTime: STALE_TIME['24Hours'], // 24 hour
  })
}

export function useCustomersDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ orgID, driverID }: { orgID: string; driverID: string }) =>
      customersApi.delete({ orgID, driverID }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.customers.all,
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: customersApi.update,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.customers.id(variables.driverID ?? ''),
      })
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.customers.all,
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useInfiniteCustomers({ params, enabled }: HookParams<VehicleListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.customers.infinite(params),
    queryFn: ({ pageParam }) =>
      customersApi.listCursor({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'], //24 Hours
  })
}
